import { Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { NotificationsService } from "angular2-notifications";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, interval, lastValueFrom, timer } from "rxjs";
import { environment } from "src/environments/environment";
import { apiCallWrapper } from "../../api/api.util";
import { VersionApi } from "../../api/version/version.api";
import { logger } from "../../util/Logger";
import { BaseService } from "../base.service";
import { StorageServiceKey } from "../storage.service";

@Injectable({
  providedIn: "root",
})
/**
 * @description This service is used to manage version API.
 */
export class VersionService extends BaseService {
  // Add BehaviorSubject for showing the update modal
  public showUpdateModalSubject = new BehaviorSubject<boolean>(false);

  public requiresRefresh = false;
  public forceUpdate = false;
  private readonly LOCAL_VERSION: string;
  private versionTimerSubscription: any;
  private snoozeTimerSubscription: any;
  private isSnoozing = false;

  constructor(
    private notifications: NotificationsService,
    private versionApi: VersionApi,
    public cookieService: CookieService, //using this to persist the number of retries for version check in case of issues reaching the API
  ) {
    super();

    if (!environment.featureFlags.versionChecking) return; // If version checking is disabled, skip all version check logic

    this.LOCAL_VERSION = environment.version || "-1"; //environment.version is set as part of CI\CD process

    // Check the version immediately on service initialization but in case there is an issue downloading the latest version
    // don't keep retrying indefinitely, only retry a few times to avoid spamming the user with notifications if there is a persistent issue.
    const versionCheckRetries =
      parseInt(
        this.cookieService.get(StorageServiceKey.versionCheckRetries) as unknown as string,
        10,
      ) || -1;

    if (versionCheckRetries === -1) {
      // First time checking version, initialize the retry count
      this._setRetries(0);
    }

    if (versionCheckRetries < 3) {
      this._setRetries(
        (parseInt(
          this.cookieService.get(StorageServiceKey.versionCheckRetries) as unknown as string,
          10,
        ) || 0) + 1,
      );
      this._checkVersion().then(() => {
        if (this.requiresRefresh) {
          window.location.reload();
        }
      });
    }

    // Set up interval to check version every 5 minutes
    this.versionTimerSubscription = interval(300000).subscribe({
      next: () => {
        if (!this.isSnoozing) {
          this._checkVersion().then(() => {
            if (this.requiresRefresh && !this.showUpdateModalSubject.value) {
              this.showUpdateModalSubject.next(true); // Trigger modal if it's not already shown and new version is available
            }
          });
        }
      },
    });
  }

  /**
   * @description Method to dismiss the modal
   */
  public dismissUpdateModal() {
    this.showUpdateModalSubject.next(false);
  }

  /**
   * @description Stop checking for version updates.  Used for external users.
   */
  public stopCheckingVersion() {
    if (this.versionTimerSubscription) {
      this.versionTimerSubscription.unsubscribe();
    }
  }

  /**
   * @description Method to snooze the version checking for 30 minutes
   */
  public snoozeVersionUpdate() {
    // Set snoozing flag
    this.isSnoozing = true;

    // Dismiss the current modal
    this.dismissUpdateModal();

    // Clear any existing snooze timer
    if (this.snoozeTimerSubscription) {
      this.snoozeTimerSubscription.unsubscribe();
    }

    // Set up 30-minute snooze timer
    this.snoozeTimerSubscription = timer(30 * 60 * 1000).subscribe({
      next: () => {
        this.isSnoozing = false;
      },
    });
  }

  /**
   * @description Checks the current version of the application against the version returned by the API.
   * If the version returned by the API is higher than the current version, it will return true, indicating that an update is required.
   */
  private async _checkVersion(): Promise<void> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.versionApi.checkVersion(this.LOCAL_VERSION), {
          notificationsService: this.notifications,
          action: "Checking for software updates",
        }),
      );

      if (data && data.isCurrentVersion !== undefined) {
        if (!data.isCurrentVersion) {
          // The current version is not the latest, force update
          this.forceUpdate = data.forceUpdate;
          this.requiresRefresh = true;
        } else {
          this._setRetries(0); // Reset retry count on successful check
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      logger.error("Error checking version", error);
      // In case of an error, we don't want to block the user from using the application, so we won't set requiresRefresh to true.
      // However, you might want to notify the user that the version check failed and they might not be on the latest version.
      this.notifications.error(
        "Failed to check for updates. You may not be on the latest version.",
      );
    }
  }

  /**
   * @description Helper method to set the number of retries for version check in the cookie.  This ensures is persistent across page reloads and browser sessions, but will reset after 30 days or if the user clears their cookies.
   * @param count The number of retries to set
   */
  private _setRetries(count: number) {
    this.cookieService.set(
      StorageServiceKey.versionCheckRetries,
      count.toString(),
      30,
      "/",
      environment.siteDomain,
      true,
      "Strict",
    );
  }
}
